import { Configuration } from '~/core/configuration/configuration.type';
import { QuerySetConfig } from '../configuration/configuration-set.type';
import { sortFiltersStateByOrder } from '../features/filtering/filterUtils';
import { GeneralFilter } from '../features/filtering/GeneralFilter';
import { IndexFilter } from '../features/indexFiltering/IndexFilter';
import { InitialPagination } from '../features/pagination/types/initialPagination.type';
import { Snapshot } from '../persistence/Snapshot';
import { SnapshotSets } from '../persistence/types/snapshot-sets';
import { State } from './types/state';
import { QuerySet_ControlsState, QuerySets_ControlsState } from './types/state-controls.type';
import { Results_State } from './types/state-results.schema';
import { QuerySets_State } from './types/state-sets.type';

const filter = new GeneralFilter();
const indexFilter = new IndexFilter();

export function createInitialState({
    config,
    searchQuery = '',
    snapshotSets = null,
    initialPagination = null,
}: {
    config: Configuration;
    searchQuery: string;
    snapshotSets: SnapshotSets | null;
    initialPagination: InitialPagination | null;
}): State {
    const useInstantSearch = config.layout.configuration.instantSearch?.useInstantSearch;
    return {
        searchQuery: searchQuery,
        sets: createSetsState(config),
        controls: createControls(config, snapshotSets),
        results: createResults(),
        config: config.layout.configuration,
        error: null,
        isLoading: false,
        loadingFinished: false,
        initialPagination,
        isInstantSearch: useInstantSearch ? true : false,
        resultLayout: config.layout.configuration.resultLayout?.defaultLayout || 'grid',
        showLayoutSwitcher: config.layout.configuration.resultLayout?.showLayoutSwitcher?? false,
    };
}

function createResults(): Results_State {
    return {
        totalRecordsCount: 0,
        filteredCategoriesRecordsCount: 0,
        sets: [],
    };
}

function createSetsState(config: Configuration): QuerySets_State {
    return config.layout.search.map((querySet) => ({
        ...querySet,
        filters: querySet.filters.map(filter.createStateFromConfig).sort(sortFiltersStateByOrder),
        indices: querySet?.indices?.map(indexFilter.createStateFromConfig),
    }));
}

function createControls(config: Configuration, snapshotSets: SnapshotSets | null): QuerySets_ControlsState {
    const snapshot = new Snapshot();

    if (snapshotSets) {
        // sets from history must correspond to the current config
        // filter out snapshot sets that are not in the current config
        const filteredSnapshotSets = snapshotSets.filter((snapshotSet) => {
            return config.layout.search.some(
                (querySet) => querySet.querySet.identifier === snapshotSet.querySetIdentifier
            );
        });
        if (filteredSnapshotSets.length > 0) {
            return snapshot.createControlsStateFromSnapshot(filteredSnapshotSets, config.layout.search);
        }
    }

    return config.layout.search.map(createSetControlsFromConfig);
}

export const createSetControlsFromConfig = (querySet: QuerySetConfig): QuerySet_ControlsState => ({
    querySet: querySet.querySet,
    filters: querySet.filters.map(filter.createControlsStateFromConfig),
    indices: querySet.indices?.map(indexFilter.createControlsStateFromConfig),
    sorting: null,
});
